import { SeededRandom } from "../core/random";
import type { GameHud, GameInstance, GameServices } from "../core/types";
import { drawPanel, drawScanlines, roundedRectPath, setText } from "./drawing";

const COLUMNS = 8;
const ROWS = 9;
const CELL_SIZE = 58;
const BOARD_X = 744;
const BOARD_Y = 86;
const ROUND_SECONDS = 60;
const kinds = [
  { label: "PUB", color: "#42e2c3", symbol: "P" },
  { label: "INT", color: "#8b72ff", symbol: "I" },
  { label: "SEC", color: "#2fe3ff", symbol: "S" },
] as const;

interface DataCell {
  kind: number;
  flagged: boolean;
}

interface FallingCell extends DataCell {
  column: number;
  row: number;
}

export class StackAndBlockGame implements GameInstance {
  private readonly random = new SeededRandom(Date.now());
  private readonly board: (DataCell | null)[][] = Array.from({ length: ROWS }, () =>
    Array<DataCell | null>(COLUMNS).fill(null),
  );
  private active: FallingCell;
  private score = 0;
  private time = ROUND_SECONDS;
  private elapsed = 0;
  private fallAccumulator = 0;
  private moveCooldown = 0;
  private message = "Build groups of 3+ matching data cards";
  private messageTimer = 0;
  private ended = false;

  constructor(private readonly services: GameServices) {
    this.seedQueue();
    this.active = this.createPiece();
  }

  advanceTime(deltaSeconds: number): void {
    if (this.ended) return;
    this.elapsed += deltaSeconds;
    this.time = Math.max(0, ROUND_SECONDS - this.elapsed);
  }

  update(deltaSeconds: number): void {
    if (this.ended) return;

    this.messageTimer = Math.max(0, this.messageTimer - deltaSeconds);
    if (this.messageTimer === 0) this.message = "Build groups of 3+ matching data cards";
    if (this.time === 0) {
      this.finish("Policy queue secured");
      return;
    }

    this.updateHorizontalMovement(deltaSeconds);

    if (
      this.services.input.consumePressed("primary") ||
      this.services.input.consumePressed("down")
    ) {
      this.hardDrop();
      return;
    }

    const speedMultiplier = this.services.input.isDown("down") ? 5 : 1;
    const fallInterval = Math.max(0.2, 0.68 - this.elapsed * 0.006);
    this.fallAccumulator += deltaSeconds * speedMultiplier;
    while (this.fallAccumulator >= fallInterval && !this.ended) {
      this.fallAccumulator -= fallInterval;
      this.stepDown();
    }
  }

  render(context: CanvasRenderingContext2D): void {
    const background = context.createLinearGradient(0, 0, 1280, 720);
    background.addColorStop(0, "#07171b");
    background.addColorStop(0.58, "#0b2130");
    background.addColorStop(1, "#151535");
    context.fillStyle = background;
    context.fillRect(0, 0, 1280, 720);
    drawScanlines(context);

    this.drawPolicyPanel(context);
    this.drawBoard(context);
    this.drawActivePiece(context);
    this.drawFooter(context);
  }

  getHud(): GameHud {
    return {
      score: this.score,
      time: this.time,
      message: this.message,
      ended: this.ended,
    };
  }

  destroy(): void {}

  private seedQueue(): void {
    for (let row = ROWS - 2; row < ROWS; row += 1) {
      for (let column = 0; column < COLUMNS; column += 1) {
        if (this.random.next() < 0.22) continue;
        this.board[row]![column] = {
          kind: this.random.integer(kinds.length),
          flagged: this.random.next() < 0.12,
        };
      }
    }
  }

  private createPiece(): FallingCell {
    return {
      column: Math.floor(COLUMNS / 2),
      row: -1,
      kind: this.random.integer(kinds.length),
      flagged: this.random.next() < 0.28,
    };
  }

  private updateHorizontalMovement(deltaSeconds: number): void {
    this.moveCooldown = Math.max(0, this.moveCooldown - deltaSeconds);
    const leftPressed = this.services.input.consumePressed("left");
    const rightPressed = this.services.input.consumePressed("right");
    let direction = 0;

    if (leftPressed) direction = -1;
    else if (rightPressed) direction = 1;
    else if (this.moveCooldown === 0) {
      if (this.services.input.isDown("left")) direction = -1;
      else if (this.services.input.isDown("right")) direction = 1;
    }

    if (direction === 0) return;
    const nextColumn = this.active.column + direction;
    if (this.canOccupy(nextColumn, this.active.row)) {
      this.active.column = nextColumn;
      this.services.audio.play("move");
    }
    this.moveCooldown = 0.13;
  }

  private canOccupy(column: number, row: number): boolean {
    if (column < 0 || column >= COLUMNS || row >= ROWS) return false;
    if (row < 0) return true;
    return this.board[row]?.[column] === null;
  }

  private stepDown(): void {
    if (this.canOccupy(this.active.column, this.active.row + 1)) {
      this.active.row += 1;
      return;
    }
    this.landPiece();
  }

  private hardDrop(): void {
    let distance = 0;
    while (this.canOccupy(this.active.column, this.active.row + 1)) {
      this.active.row += 1;
      distance += 1;
    }
    this.score += distance * 2;
    this.landPiece();
  }

  private landPiece(): void {
    if (this.active.row < 0) {
      this.finish("Policy queue overflowed");
      return;
    }

    this.board[this.active.row]![this.active.column] = {
      kind: this.active.kind,
      flagged: this.active.flagged,
    };
    const cleared = this.resolveMatches();
    if (cleared === 0 && this.active.flagged) {
      this.setMessage("Flagged payload queued - match its class", 2.2);
      this.services.audio.play("miss");
    } else if (cleared > 0) {
      this.setMessage(`Policy matched - ${cleared} cards cleared`, 2.2);
      this.services.audio.play("hit");
    } else {
      this.services.audio.play("move");
    }

    this.active = this.createPiece();
    this.fallAccumulator = 0;
    if (!this.canOccupy(this.active.column, 0)) this.finish("Policy queue overflowed");
  }

  private resolveMatches(): number {
    let totalCleared = 0;
    let combo = 0;

    while (true) {
      const visited = new Set<string>();
      const marked = new Set<string>();

      for (let row = 0; row < ROWS; row += 1) {
        for (let column = 0; column < COLUMNS; column += 1) {
          const key = `${row}:${column}`;
          const cell = this.board[row]?.[column];
          if (!cell || visited.has(key)) continue;
          const group = this.collectGroup(row, column, cell.kind, visited);
          if (group.length >= 3) for (const member of group) marked.add(member);
        }
      }

      if (marked.size === 0) return totalCleared;

      let flaggedCount = 0;
      for (const key of marked) {
        const [rowText, columnText] = key.split(":");
        const row = Number(rowText);
        const column = Number(columnText);
        const cell = this.board[row]?.[column];
        if (cell?.flagged) flaggedCount += 1;
        if (this.board[row]) this.board[row]![column] = null;
      }
      this.compactBoard();
      totalCleared += marked.size;
      this.score += marked.size * 40 + flaggedCount * 120 + combo * 75;
      combo += 1;
    }
  }

  private collectGroup(
    startRow: number,
    startColumn: number,
    kind: number,
    visited: Set<string>,
  ): string[] {
    const group: string[] = [];
    const pending: Array<readonly [number, number]> = [[startRow, startColumn]];

    while (pending.length > 0) {
      const point = pending.pop();
      if (!point) break;
      const [row, column] = point;
      const key = `${row}:${column}`;
      if (visited.has(key)) continue;
      if (this.board[row]?.[column]?.kind !== kind) continue;
      visited.add(key);
      group.push(key);

      if (row > 0) pending.push([row - 1, column]);
      if (row < ROWS - 1) pending.push([row + 1, column]);
      if (column > 0) pending.push([row, column - 1]);
      if (column < COLUMNS - 1) pending.push([row, column + 1]);
    }
    return group;
  }

  private compactBoard(): void {
    for (let column = 0; column < COLUMNS; column += 1) {
      const cells: DataCell[] = [];
      for (let row = ROWS - 1; row >= 0; row -= 1) {
        const cell = this.board[row]?.[column];
        if (cell) cells.push(cell);
      }
      for (let row = ROWS - 1; row >= 0; row -= 1) {
        this.board[row]![column] = cells[ROWS - 1 - row] ?? null;
      }
    }
  }

  private setMessage(message: string, duration: number): void {
    this.message = message;
    this.messageTimer = duration;
    this.services.announce(message);
  }

  private finish(message: string): void {
    this.ended = true;
    this.message = message;
    this.services.audio.play("finish");
    this.services.announce(`${message}. Final score ${this.score}.`);
  }

  private drawPolicyPanel(context: CanvasRenderingContext2D): void {
    drawPanel(context, 56, 78, 610, 470, "#ff7a1a");
    setText(context, 14, 850, "#9feddd");
    context.fillText("DLP POLICY ENGINE", 92, 116);
    setText(context, 44, 900, "#f7fbff");
    context.fillText("Classify the queue", 92, 165);
    setText(context, 18, 650, "#a9bbc4");
    context.fillText("Connect 3+ cards with the same classification.", 92, 205);
    context.fillText("Flagged cards earn a policy bonus when cleared.", 92, 234);

    setText(context, 12, 850, "#839aa5");
    context.fillText("ACTIVE PAYLOAD", 92, 292);
    this.drawCell(context, 92, 318, 104, this.active);

    setText(context, 12, 850, "#839aa5");
    context.fillText("POLICY CLASSES", 246, 292);
    for (let index = 0; index < kinds.length; index += 1) {
      const kind = kinds[index];
      if (!kind) continue;
      context.fillStyle = kind.color;
      roundedRectPath(context, 246, 318 + index * 48, 34, 34, 8);
      context.fill();
      setText(context, 14, 850, "#f7fbff");
      context.fillText(`${kind.symbol}  ${kind.label}`, 296, 335 + index * 48);
    }

    const fill = this.highestFilledRow();
    setText(context, 12, 850, "#839aa5");
    context.fillText("QUEUE PRESSURE", 92, 460);
    context.fillStyle = "rgba(255, 255, 255, 0.1)";
    roundedRectPath(context, 92, 480, 516, 15, 7);
    context.fill();
    const pressure = fill < 0 ? 0 : (ROWS - fill) / ROWS;
    const pressureColor = pressure > 0.7 ? "#ff5d5d" : "#42e2c3";
    context.fillStyle = pressureColor;
    roundedRectPath(context, 92, 480, Math.max(3, 516 * pressure), 15, 7);
    context.fill();
  }

  private highestFilledRow(): number {
    for (let row = 0; row < ROWS; row += 1) {
      if (this.board[row]?.some((cell) => cell !== null)) return row;
    }
    return -1;
  }

  private drawBoard(context: CanvasRenderingContext2D): void {
    context.save();
    roundedRectPath(
      context,
      BOARD_X - 18,
      BOARD_Y - 18,
      COLUMNS * CELL_SIZE + 36,
      ROWS * CELL_SIZE + 36,
      24,
    );
    context.fillStyle = "rgba(3, 12, 18, 0.76)";
    context.fill();
    context.strokeStyle = "rgba(66, 226, 195, 0.5)";
    context.lineWidth = 3;
    context.stroke();

    for (let row = 0; row < ROWS; row += 1) {
      for (let column = 0; column < COLUMNS; column += 1) {
        const x = BOARD_X + column * CELL_SIZE;
        const y = BOARD_Y + row * CELL_SIZE;
        context.strokeStyle = "rgba(255, 255, 255, 0.07)";
        context.strokeRect(x + 3, y + 3, CELL_SIZE - 6, CELL_SIZE - 6);
        const cell = this.board[row]?.[column];
        if (cell) this.drawCell(context, x + 5, y + 5, CELL_SIZE - 10, cell);
      }
    }

    const scanY = this.services.reducedMotion
      ? BOARD_Y + ROWS * CELL_SIZE * 0.55
      : BOARD_Y + ((this.elapsed * 92) % (ROWS * CELL_SIZE));
    const scanGradient = context.createLinearGradient(BOARD_X, 0, BOARD_X + COLUMNS * CELL_SIZE, 0);
    scanGradient.addColorStop(0, "rgba(66, 226, 195, 0)");
    scanGradient.addColorStop(0.5, "rgba(66, 226, 195, 0.9)");
    scanGradient.addColorStop(1, "rgba(66, 226, 195, 0)");
    context.strokeStyle = scanGradient;
    context.lineWidth = 2;
    context.beginPath();
    context.moveTo(BOARD_X, scanY);
    context.lineTo(BOARD_X + COLUMNS * CELL_SIZE, scanY);
    context.stroke();
    context.restore();
  }

  private drawActivePiece(context: CanvasRenderingContext2D): void {
    if (this.active.row < -0.8) return;
    const x = BOARD_X + this.active.column * CELL_SIZE + 5;
    const y = BOARD_Y + this.active.row * CELL_SIZE + 5;
    context.save();
    context.shadowColor = kinds[this.active.kind]?.color ?? "#42e2c3";
    context.shadowBlur = 18;
    this.drawCell(context, x, y, CELL_SIZE - 10, this.active);
    context.restore();
  }

  private drawCell(
    context: CanvasRenderingContext2D,
    x: number,
    y: number,
    size: number,
    cell: DataCell,
  ): void {
    const kind = kinds[cell.kind] ?? kinds[0];
    context.save();
    roundedRectPath(context, x, y, size, size, Math.max(8, size * 0.16));
    context.fillStyle = cell.flagged ? "#38202a" : "#102630";
    context.fill();
    context.strokeStyle = cell.flagged ? "#ff7a1a" : kind.color;
    context.lineWidth = Math.max(2, size * 0.035);
    context.stroke();

    if (cell.flagged) {
      context.strokeStyle = "rgba(255, 122, 26, 0.25)";
      context.lineWidth = 2;
      for (let offset = -size; offset < size * 2; offset += 13) {
        context.beginPath();
        context.moveTo(x + offset, y + size);
        context.lineTo(x + offset + size, y);
        context.stroke();
      }
    }

    setText(context, Math.max(13, size * 0.27), 900, "#f7fbff", "center");
    context.fillText(kind.symbol, x + size / 2, y + size / 2);
    if (cell.flagged) {
      context.fillStyle = "#ff7a1a";
      context.beginPath();
      context.moveTo(x + size - 16, y + 5);
      context.lineTo(x + size - 5, y + 25);
      context.lineTo(x + size - 27, y + 25);
      context.closePath();
      context.fill();
      setText(context, 11, 900, "#071218", "center");
      context.fillText("!", x + size - 16, y + 19);
    }
    context.restore();
  }

  private drawFooter(context: CanvasRenderingContext2D): void {
    drawPanel(context, 56, 578, 1168, 88, "#ff7a1a");
    setText(context, 12, 850, "#9feddd");
    context.fillText("OBJECTIVE", 92, 608);
    setText(context, 25, 850, "#f7fbff");
    context.fillText(this.message, 92, 640);
    setText(context, 13, 750, "#9cb2bd", "right");
    context.fillText("MOVE  ← →     DROP  SPACE", 1188, 640);
  }
}
